import { useState } from 'react'
import {
  Upload,
  FileText,
  Shield,
  AlertTriangle,
  BarChart3,
  TrendingUp,
  Zap,
  Search,
  Download,
  CheckCircle,
  Activity,
  Network,
  HardDrive,
  Terminal,
  Eye,
  Settings,
  Clock,
  Cpu,
  Lock,
  Globe,
  Code2,
  FileCode,
  Trash2,
  AlertCircle,
} from 'lucide-react'

export default function SandboxAnalysisPage() {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [selectedAnalysis, setSelectedAnalysis] = useState(null)

  const stats = {
    totalAnalysis: 3847,
    malwareDetected: 234,
    cleanFiles: 3421,
    suspiciousFiles: 192,
    avgAnalysisTime: '2m 34s',
    threatLevel: 'Medium',
  }

  const recentAnalyses = [
    {
      id: 1,
      filename: 'document.exe',
      hash: 'a1b2c3d4e5f6g7h8i9j0',
      uploadTime: '2024-04-25 14:32:00',
      status: 'Malware Detected',
      threatLevel: 'Critical',
      score: 92,
      type: 'PE32',
      fileSize: '2.4 MB',
      detectedBy: 'Multiple engines',
      verdict: 'Trojan.Win32.Generic',
    },
    {
      id: 2,
      filename: 'image.jpg',
      hash: 'b2c3d4e5f6g7h8i9j0k1',
      uploadTime: '2024-04-25 13:15:00',
      status: 'Suspicious',
      threatLevel: 'High',
      score: 68,
      type: 'JPEG',
      fileSize: '1.2 MB',
      detectedBy: 'Heuristic analysis',
      verdict: 'Potentially Unwanted App',
    },
    {
      id: 3,
      filename: 'archive.zip',
      hash: 'c3d4e5f6g7h8i9j0k1l2',
      uploadTime: '2024-04-25 12:45:00',
      status: 'Clean',
      threatLevel: 'Low',
      score: 8,
      type: 'ZIP',
      fileSize: '45.3 MB',
      detectedBy: 'All engines clean',
      verdict: 'Clean - Safe to open',
    },
    {
      id: 4,
      filename: 'script.ps1',
      hash: 'd4e5f6g7h8i9j0k1l2m3',
      uploadTime: '2024-04-25 11:20:00',
      status: 'Suspicious',
      threatLevel: 'Medium',
      score: 52,
      type: 'PowerShell Script',
      fileSize: '24.5 KB',
      detectedBy: 'Behavioral analysis',
      verdict: 'Suspicious Script Activity',
    },
    {
      id: 5,
      filename: 'installer.msi',
      hash: 'e5f6g7h8i9j0k1l2m3n4',
      uploadTime: '2024-04-25 10:05:00',
      status: 'Clean',
      threatLevel: 'Low',
      score: 5,
      type: 'MSI Installer',
      fileSize: '156.7 MB',
      detectedBy: 'All engines clean',
      verdict: 'Clean - Legitimate software',
    },
  ]

  const analysisDetails = {
    staticAnalysis: [
      { category: 'File Properties', value: 'PE32, 64-bit executable' },
      { category: 'File Size', value: '2.4 MB' },
      { category: 'Compilation Date', value: '2024-01-15 08:23:45' },
      { category: 'Sections', value: '.text, .data, .rsrc, .reloc' },
      { category: 'Imports', value: 'kernel32.dll, ntdll.dll, user32.dll' },
      { category: 'Exports', value: 'None detected' },
      { category: 'Digital Signature', value: 'Not signed' },
      { category: 'PDB Path', value: 'C:\\build\\malware\\bin\\Release' },
    ],
    dynamicAnalysis: [
      { process: 'explorer.exe', action: 'Child process spawned', severity: 'Medium' },
      { process: 'svchost.exe', action: 'Registry modification attempted', severity: 'High' },
      { process: 'system.exe', action: 'File creation in System32', severity: 'Critical' },
      { process: 'cmd.exe', action: 'Command shell invoked', severity: 'High' },
      { process: 'powershell.exe', action: 'Script execution detected', severity: 'High' },
      { process: 'rundll32.exe', action: 'DLL loaded from temp folder', severity: 'Medium' },
    ],
    networkAnalysis: [
      { type: 'DNS', target: 'malicious-domain.com', action: 'Domain lookup', severity: 'Critical' },
      { type: 'HTTP', target: 'http://c2-server.ru:8080', action: 'C2 communication', severity: 'Critical' },
      { type: 'HTTPS', target: 'https://exfil-server.com/data', action: 'Data exfiltration', severity: 'Critical' },
      { type: 'DNS', target: 'update.microsoft-clone.net', action: 'Suspicious domain', severity: 'High' },
    ],
    fileSystemChanges: [
      { path: 'C:\\Windows\\System32\\malware.dll', action: 'File created', severity: 'Critical' },
      { path: 'HKLM\\Software\\Microsoft\\Windows\\Run', action: 'Registry persistence', severity: 'Critical' },
      { path: 'C:\\ProgramData\\virus.exe', action: 'Executable dropped', severity: 'High' },
      { path: '%AppData%\\Local\\Temp\\payload.dat', action: 'Temp file created', severity: 'Medium' },
    ],
  }

  const detectionEngines = [
    { engine: 'Kaspersky', verdict: 'Malware', confidence: 99 },
    { engine: 'McAfee', verdict: 'Trojan.Generic', confidence: 98 },
    { engine: 'Avast', verdict: 'Win32:Malware', confidence: 97 },
    { engine: 'Norton', verdict: 'Trojan.Win32', confidence: 96 },
    { engine: 'Bitdefender', verdict: 'Malware', confidence: 99 },
    { engine: 'ESET', verdict: 'Trojan', confidence: 98 },
  ]

  const capabilities = [
    { name: 'File Upload & Analysis', status: 'Active', maxSize: '500 MB' },
    { name: 'Malware Detection', status: 'Active', engines: '60+' },
    { name: 'Behavioral Analysis', status: 'Active', detection: '99.2%' },
    { name: 'Dynamic Execution', status: 'Active', timeout: '60 sec' },
    { name: 'Network Monitoring', status: 'Active', traffic: 'Full capture' },
    { name: 'URL & Link Analysis', status: 'Active', scanning: 'Real-time' },
  ]

  const features = [
    {
      icon: Upload,
      title: 'File Upload & Analysis',
      description: 'Submit files up to 500MB for deep analysis in isolated sandbox environment',
    },
    {
      icon: Cpu,
      title: 'Dynamic Execution',
      description: 'Monitor process behavior, system calls, and memory modifications during execution',
    },
    {
      icon: Network,
      title: 'Network Monitoring',
      description: 'Track all network communications including DNS, HTTP, HTTPS, and custom protocols',
    },
    {
      icon: HardDrive,
      title: 'File System Tracking',
      description: 'Monitor file creation, deletion, and registry modifications in real-time',
    },
    {
      icon: Code2,
      title: 'Static Analysis',
      description: 'Deep analysis of file structure, imports, exports, and digital signatures',
    },
    {
      icon: Globe,
      title: 'URL & Link Analysis',
      description: 'Analyze suspicious URLs and detect phishing, malware distribution sites',
    },
    {
      icon: Activity,
      title: 'Process Monitoring',
      description: '60+ AV engines detection with heuristic analysis and behavioral patterns',
    },
    {
      icon: AlertTriangle,
      title: 'Threat Intelligence',
      description: 'Cross-reference with 500M+ known malware signatures and threat databases',
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-black text-white mb-2">Sandbox Analysis System</h1>
            <p className="text-slate-400">Advanced Malware Analysis & Threat Detection Platform</p>
          </div>
          <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all">
            <Upload size={20} className="inline mr-2" />
            Upload File
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Analyses</p>
                <p className="text-2xl font-black text-white">{stats.totalAnalysis}</p>
              </div>
              <FileText size={32} className="text-blue-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-red-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Malware Detected</p>
                <p className="text-2xl font-black text-red-400">{stats.malwareDetected}</p>
              </div>
              <AlertTriangle size={32} className="text-red-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-green-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Clean Files</p>
                <p className="text-2xl font-black text-green-400">{stats.cleanFiles}</p>
              </div>
              <CheckCircle size={32} className="text-green-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-yellow-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Suspicious</p>
                <p className="text-2xl font-black text-yellow-400">{stats.suspiciousFiles}</p>
              </div>
              <AlertCircle size={32} className="text-yellow-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Avg Analysis Time</p>
                <p className="text-xl font-black text-white">{stats.avgAnalysisTime}</p>
              </div>
              <Clock size={32} className="text-cyan-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-4 rounded-lg border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Overall Threat Level</p>
                <p className="text-xl font-black text-orange-400">{stats.threatLevel}</p>
              </div>
              <Shield size={32} className="text-orange-400 opacity-50" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-blue-500/20 pb-4 overflow-x-auto">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
          { id: 'analyses', label: 'Recent Analyses', icon: FileText },
          { id: 'details', label: 'Analysis Details', icon: Eye },
          { id: 'capabilities', label: 'Capabilities', icon: Settings },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-blue-600/40 to-cyan-600/40 text-blue-200 border border-blue-500/50'
                : 'text-slate-400 hover:text-blue-300'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* File Upload Area */}
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-12 rounded-lg border-2 border-dashed border-blue-500/30 hover:border-blue-500/50 transition-all text-center cursor-pointer">
            <Upload size={48} className="mx-auto text-blue-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Drag & Drop to Upload</h3>
            <p className="text-slate-400 mb-4">or click to select files (up to 500 MB)</p>
            <button className="px-6 py-2 bg-blue-600/20 text-blue-300 rounded-lg hover:bg-blue-600/30 transition-all">
              Browse Files
            </button>
          </div>

          {/* Features Grid */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">Sandbox Capabilities</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-6 rounded-lg border border-blue-500/20 hover:border-blue-500/40 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-600/20 rounded-lg">
                      <feature.icon size={24} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1">{feature.title}</h3>
                      <p className="text-sm text-slate-400">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Capabilities Status */}
          <div>
            <h2 className="text-2xl font-black text-white mb-4">System Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {capabilities.map((cap, idx) => (
                <div key={idx} className="bg-slate-800/50 p-4 rounded-lg border border-green-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-white">{cap.name}</h3>
                    <span className="text-xs px-2 py-1 bg-green-600/20 text-green-300 rounded-full">
                      {cap.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">
                      {cap.maxSize || cap.engines || cap.detection || cap.timeout || cap.traffic || cap.scanning}
                    </span>
                    <CheckCircle size={16} className="text-green-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recent Analyses Tab */}
      {activeTab === 'analyses' && (
        <div className="space-y-4">
          {recentAnalyses.map(analysis => (
            <div key={analysis.id} className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20 hover:border-blue-500/40 transition-all cursor-pointer" onClick={() => setSelectedAnalysis(analysis)}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`p-3 rounded-lg ${
                    analysis.threatLevel === 'Critical'
                      ? 'bg-red-600/20'
                      : analysis.threatLevel === 'High'
                      ? 'bg-orange-600/20'
                      : analysis.threatLevel === 'Medium'
                      ? 'bg-yellow-600/20'
                      : 'bg-green-600/20'
                  }`}>
                    <FileText className={`${
                      analysis.threatLevel === 'Critical'
                        ? 'text-red-400'
                        : analysis.threatLevel === 'High'
                        ? 'text-orange-400'
                        : analysis.threatLevel === 'Medium'
                        ? 'text-yellow-400'
                        : 'text-green-400'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white">{analysis.filename}</h3>
                    <p className="text-sm text-slate-400 font-mono">{analysis.hash}</p>
                    <div className="flex gap-4 mt-2 text-sm text-slate-400">
                      <span>{analysis.fileSize}</span>
                      <span>•</span>
                      <span>{analysis.type}</span>
                      <span>•</span>
                      <span>{analysis.uploadTime}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-black ${
                    analysis.threatLevel === 'Critical'
                      ? 'text-red-400'
                      : analysis.threatLevel === 'High'
                      ? 'text-orange-400'
                      : analysis.threatLevel === 'Medium'
                      ? 'text-yellow-400'
                      : 'text-green-400'
                  }`}>
                    {analysis.score}
                  </p>
                  <p className="text-xs text-slate-400">Threat Score</p>
                </div>
              </div>

              <div className="border-t border-slate-700 pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-semibold ${
                      analysis.threatLevel === 'Critical'
                        ? 'text-red-400'
                        : analysis.threatLevel === 'High'
                        ? 'text-orange-400'
                        : analysis.threatLevel === 'Medium'
                        ? 'text-yellow-400'
                        : 'text-green-400'
                    }`}>
                      {analysis.verdict}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{analysis.detectedBy}</p>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full ${
                    analysis.status === 'Malware Detected'
                      ? 'bg-red-600/20 text-red-300'
                      : analysis.status === 'Suspicious'
                      ? 'bg-yellow-600/20 text-yellow-300'
                      : 'bg-green-600/20 text-green-300'
                  }`}>
                    {analysis.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Analysis Details Tab */}
      {activeTab === 'details' && (
        <div className="space-y-6">
          {selectedAnalysis ? (
            <>
              <div className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20">
                <h2 className="text-2xl font-black text-white mb-4">File: {selectedAnalysis.filename}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">SHA256 Hash</p>
                    <p className="font-mono text-white break-all">{selectedAnalysis.hash}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Upload Time</p>
                    <p className="text-white">{selectedAnalysis.uploadTime}</p>
                  </div>
                </div>
              </div>

              {/* Detection Engines */}
              <div>
                <h2 className="text-2xl font-black text-white mb-4">Detection Results ({detectionEngines.length} engines)</h2>
                <div className="space-y-3">
                  {detectionEngines.map((engine, idx) => (
                    <div key={idx} className="bg-slate-800/50 p-4 rounded-lg border border-blue-500/20 flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-white">{engine.engine}</h3>
                        <p className="text-sm text-slate-400">{engine.verdict}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-black text-red-400">{engine.confidence}%</p>
                        <p className="text-xs text-slate-400">Confidence</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Analysis Sections */}
              {[
                { title: 'Static Analysis', icon: Code2, data: analysisDetails.staticAnalysis },
                { title: 'Dynamic Analysis', icon: Activity, data: analysisDetails.dynamicAnalysis },
                { title: 'Network Analysis', icon: Network, data: analysisDetails.networkAnalysis },
                { title: 'File System Changes', icon: HardDrive, data: analysisDetails.fileSystemChanges },
              ].map((section, idx) => (
                <div key={idx}>
                  <h2 className="text-2xl font-black text-white mb-4 flex items-center gap-2">
                    <section.icon size={28} className="text-blue-400" />
                    {section.title}
                  </h2>
                  <div className="space-y-2">
                    {section.data.map((item, i) => (
                      <div key={i} className={`bg-slate-800/50 p-3 rounded-lg border border-blue-500/20 flex justify-between items-start ${
                        item.severity === 'Critical' ? 'border-red-500/20' : item.severity === 'High' ? 'border-orange-500/20' : ''
                      }`}>
                        <div className="flex-1">
                          <p className="text-white font-semibold">{item.category || item.process || item.type || item.path}</p>
                          <p className="text-sm text-slate-400">{item.value || item.action || item.target}</p>
                        </div>
                        {item.severity && (
                          <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ml-2 ${
                            item.severity === 'Critical'
                              ? 'bg-red-600/20 text-red-300'
                              : item.severity === 'High'
                              ? 'bg-orange-600/20 text-orange-300'
                              : 'bg-yellow-600/20 text-yellow-300'
                          }`}>
                            {item.severity}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="bg-slate-800/50 p-12 rounded-lg border border-blue-500/20 text-center">
              <Eye size={48} className="mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">Select an analysis from the "Recent Analyses" tab to view details</p>
            </div>
          )}
        </div>
      )}

      {/* Capabilities Tab */}
      {activeTab === 'capabilities' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20">
            <div className="flex items-center gap-3 mb-4">
              <Cpu size={28} className="text-blue-400" />
              <h3 className="text-xl font-bold text-white">Sandbox Environment</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-slate-400">OS Support</p>
                <p className="text-white">Windows XP - Windows 11, Linux, macOS</p>
              </div>
              <div>
                <p className="text-slate-400">Max File Size</p>
                <p className="text-white">500 MB</p>
              </div>
              <div>
                <p className="text-slate-400">Execution Time</p>
                <p className="text-white">Up to 60 seconds per file</p>
              </div>
              <div>
                <p className="text-slate-400">Isolation Level</p>
                <p className="text-white">Full VM-based isolation</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20">
            <div className="flex items-center gap-3 mb-4">
              <Shield size={28} className="text-green-400" />
              <h3 className="text-xl font-bold text-white">Detection Engines</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-slate-400">Antivirus Engines</p>
                <p className="text-white">60+ real-time scanning engines</p>
              </div>
              <div>
                <p className="text-slate-400">Malware Database</p>
                <p className="text-white">500M+ known signatures</p>
              </div>
              <div>
                <p className="text-slate-400">Heuristic Analysis</p>
                <p className="text-white">Advanced behavior-based detection</p>
              </div>
              <div>
                <p className="text-slate-400">Zero-Day Protection</p>
                <p className="text-white">99.8% detection rate</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20">
            <div className="flex items-center gap-3 mb-4">
              <Network size={28} className="text-cyan-400" />
              <h3 className="text-xl font-bold text-white">Network Analysis</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-slate-400">Protocols Monitored</p>
                <p className="text-white">DNS, HTTP, HTTPS, FTP, SSH, TLS</p>
              </div>
              <div>
                <p className="text-slate-400">Domain Tracking</p>
                <p className="text-white">C2 server detection</p>
              </div>
              <div>
                <p className="text-slate-400">IP Reputation</p>
                <p className="text-white">Real-time blacklist checking</p>
              </div>
              <div>
                <p className="text-slate-400">Traffic Capture</p>
                <p className="text-white">Full PCAP export available</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 p-6 rounded-lg border border-blue-500/20">
            <div className="flex items-center gap-3 mb-4">
              <FileCode size={28} className="text-purple-400" />
              <h3 className="text-xl font-bold text-white">Reporting</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-slate-400">Report Formats</p>
                <p className="text-white">PDF, JSON, XML, HTML</p>
              </div>
              <div>
                <p className="text-slate-400">Analysis Time</p>
                <p className="text-white">Average 2-5 minutes</p>
              </div>
              <div>
                <p className="text-slate-400">Report Retention</p>
                <p className="text-white">Unlimited storage</p>
              </div>
              <div>
                <p className="text-slate-400">API Access</p>
                <p className="text-white">Full REST API available</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
